import * as THREE from 'three';
import type { ActiveToxicCloud } from '../../../shared/game/types';
import { getItemVfxSmokeTexture, ITEM_VFX_SMOKE_ALPHA_TEST } from '../vfx/items/sprite-textures';

export interface ActiveToxicCloudVisuals {
  sync(clouds: readonly ActiveToxicCloud[]): void;
  update(deltaSeconds: number): void;
  dispose(): void;
}

interface CloudSprite {
  readonly sprite: THREE.Sprite;
  readonly material: THREE.SpriteMaterial;
  readonly base: THREE.Vector3;
  readonly phase: number;
  readonly drift: THREE.Vector3;
  readonly baseOpacity: number;
  readonly baseScale: number;
}

interface CloudTilePosition {
  readonly tileId: string;
  readonly position: THREE.Vector3;
}

interface CloudRecord {
  readonly id: string;
  readonly group: THREE.Group;
  readonly sprites: CloudSprite[];
  time: number;
}

interface CreateActiveToxicCloudVisualsConfig {
  readonly parent: THREE.Group;
  readonly getPositionForNode: (nodeId: string) => THREE.Vector3 | null;
}

function hashUnit(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 0xffffffff;
}

function disposeCloudRecord(record: CloudRecord): void {
  record.group.parent?.remove(record.group);
  record.group.traverse((child) => {
    const sprite = child as THREE.Sprite;
    const material = sprite.material;
    if (material instanceof THREE.Material) material.dispose();
  });
  record.group.clear();
}

export function createActiveToxicCloudVisuals(
  config: CreateActiveToxicCloudVisualsConfig,
): ActiveToxicCloudVisuals {
  const records = new Map<string, CloudRecord>();
  let disposed = false;

  function buildCloud(cloud: ActiveToxicCloud): CloudRecord | null {
    const positions = cloud.tileIds
      .map((tileId): CloudTilePosition | null => {
        const position = config.getPositionForNode(tileId);
        return position == null ? null : { tileId, position };
      })
      .filter((entry): entry is CloudTilePosition => entry != null);
    if (positions.length === 0) return null;

    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const { position } of positions) {
      minX = Math.min(minX, position.x);
      maxX = Math.max(maxX, position.x);
      minZ = Math.min(minZ, position.z);
      maxZ = Math.max(maxZ, position.z);
    }

    const width = maxX - minX;
    const depth = maxZ - minZ;
    const center = new THREE.Vector3((minX + maxX) * 0.5, 0, (minZ + maxZ) * 0.5);
    const footprint = Math.max(2.5, Math.hypot(width, depth) + 2.4);
    const spritesPerTile = 7;

    const group = new THREE.Group();
    group.name = `active-toxic-cloud:${cloud.id}`;
    group.position.copy(center);
    group.userData.activeToxicCloudId = cloud.id;
    group.userData.tileIds = [...cloud.tileIds];
    group.userData.boundsWidth = width;
    group.userData.boundsDepth = depth;
    group.userData.visualFootprint = footprint;

    const texture = getItemVfxSmokeTexture();
    const sprites: CloudSprite[] = [];
    for (const { tileId, position } of positions) {
      const tileBase = position.clone().sub(center);
      tileBase.y += 0.62;

      for (let tileSpriteIndex = 0; tileSpriteIndex < spritesPerTile; tileSpriteIndex += 1) {
        const seed = `${cloud.id}:${tileId}:${tileSpriteIndex}`;
        const angle =
          (tileSpriteIndex / Math.max(1, spritesPerTile - 1)) * Math.PI * 2 +
          hashUnit(`${seed}:angle`) * 0.55;
        const isCenterSprite = tileSpriteIndex === 0;
        const radius = isCenterSprite ? 0 : 0.18 + hashUnit(`${seed}:radius`) * 0.72;
        const base = new THREE.Vector3(
          tileBase.x + Math.cos(angle) * radius,
          tileBase.y + (hashUnit(`${seed}:height`) - 0.5) * 0.32,
          tileBase.z + Math.sin(angle) * radius,
        );
        const material = new THREE.SpriteMaterial({
          map: texture,
          color: tileSpriteIndex % 3 === 0 ? 0x9dff63 : 0x5fcf42,
          transparent: true,
          alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
          opacity: 0.34,
          blending: THREE.NormalBlending,
          depthWrite: false,
        });
        const sprite = new THREE.Sprite(material);
        sprite.position.copy(base);
        const baseScale =
          (isCenterSprite ? 1.75 : 1.18) +
          hashUnit(`${seed}:scale`) * (isCenterSprite ? 0.28 : 0.42);
        sprite.scale.set(baseScale, baseScale, 1);
        group.add(sprite);
        sprites.push({
          sprite,
          material,
          base,
          phase: hashUnit(`${seed}:phase`) * Math.PI * 2,
          drift: new THREE.Vector3(
            Math.cos(angle + Math.PI * 0.5) * 0.11,
            0.04,
            Math.sin(angle + Math.PI * 0.5) * 0.11,
          ),
          baseOpacity: 0.23 + hashUnit(`${seed}:opacity`) * 0.19,
          baseScale,
        });
      }
    }

    config.parent.add(group);
    return { id: cloud.id, group, sprites, time: 0 };
  }

  function sync(clouds: readonly ActiveToxicCloud[]): void {
    if (disposed) return;
    const nextIds = new Set(clouds.map((cloud) => cloud.id));
    for (const [id, record] of records) {
      if (!nextIds.has(id)) {
        disposeCloudRecord(record);
        records.delete(id);
      }
    }
    for (const cloud of clouds) {
      const existing = records.get(cloud.id);
      const tileKey = cloud.tileIds.join('|');
      if (existing != null && existing.group.userData.tileKey === tileKey) continue;
      if (existing != null) {
        disposeCloudRecord(existing);
        records.delete(cloud.id);
      }
      const record = buildCloud(cloud);
      if (record != null) {
        record.group.userData.tileKey = tileKey;
        records.set(cloud.id, record);
      }
    }
  }

  function update(deltaSeconds: number): void {
    if (disposed) return;
    for (const record of records.values()) {
      record.time += deltaSeconds;
      for (const cloudSprite of record.sprites) {
        const wave = Math.sin(record.time * 0.9 + cloudSprite.phase);
        const breathe = 1 + wave * 0.07;
        cloudSprite.sprite.position.set(
          cloudSprite.base.x +
            cloudSprite.drift.x * Math.sin(record.time * 0.45 + cloudSprite.phase),
          cloudSprite.base.y +
            cloudSprite.drift.y * Math.sin(record.time * 0.8 + cloudSprite.phase),
          cloudSprite.base.z +
            cloudSprite.drift.z * Math.cos(record.time * 0.45 + cloudSprite.phase),
        );
        cloudSprite.sprite.scale.setScalar(cloudSprite.baseScale * breathe);
        cloudSprite.sprite.scale.z = 1;
        cloudSprite.material.opacity = cloudSprite.baseOpacity * (0.84 + wave * 0.16);
      }
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const record of records.values()) disposeCloudRecord(record);
    records.clear();
  }

  return { sync, update, dispose };
}
